import { FC, useEffect, useRef, useState, MouseEvent } from 'react';
import { Loader } from 'shared/ui';
import { SearchInput } from 'shared/ui/SearchInput';
import { TTheme } from 'widgets/choose-background/ui/types';
import { stockVideosStore } from 'features/list-stock-videos';
import { cn } from 'shared/lib';
import { useInView } from 'react-intersection-observer';
import Modal from 'components/shared/Modal';
import { IoMdClose } from 'react-icons/io';

interface IProps {
  theme: TTheme;
  onChange: (value: string) => void;
  setCustomVideoBackgrounds: (value: string[]) => void;
  customVideoBackgrounds: string[];
  selectedBackground: string;
}

interface StockVideoFile {
  link: string;
  width: number;
}

interface StockVideo {
  id: string | number;
  video_files: StockVideoFile[];
}

const VideosTab: FC<IProps> = ({
  theme,
  onChange,
  customVideoBackgrounds,
  selectedBackground
}) => {
  const {
    getStockVideos,
    changeQueryVideo,
    changePageVideo,
    stockVideos,
    isVideoLoaded
  } = stockVideosStore((state) => ({
    getStockVideos: state.getStockVideos,
    changeQueryVideo: state.changeQuery,
    changePageVideo: state.changePage,
    stockVideos: state.stockVideos as StockVideo[],
    isVideoLoaded: state.isLoaded
  }));

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [root, setRoot] = useState<Element | null>(null);

  useEffect(() => {
    setRoot(scrollRef.current);
  }, []);

  const { ref: sentinelRef, inView } = useInView({
    root,
    threshold: 0.5
  });

  useEffect(() => {
    getStockVideos();
  }, []);

  useEffect(() => {
    if (inView) changePageVideo();
  }, [inView]);

  const [open, setOpen] = useState(false);

  const handlePlayVideo = async (e: MouseEvent<HTMLVideoElement>) => {
    const video = e.currentTarget;
    try {
      if (video.paused && video.readyState >= 2) {
        await video.play();
      }
    } catch (error) {
      console.error('Failed to play video:', error);
    }
  };

  const handlePauseVideo = (e: MouseEvent<HTMLVideoElement>) => {
    const video = e.currentTarget;
    if (!video.paused) {
      video.pause();
    }
  };

  const handleSearchVideo = (value: string) => {
    changeQueryVideo(value || 'nature');
  };

  return (
    <>
      <div className="ag-flex ag-flex-row ag-justify-between ag-items-center ag-text-sm">
        <p className="ag-font-bold">Videos</p>
        <button
          onClick={() => setOpen(true)}
          type="button"
          className="ag-text-vivid-purple"
        >
          See all
        </button>
      </div>

      <div className="ag-grid ag-grid-cols-3 ag-gap-2">
        {selectedBackground?.startsWith('video;') && (
          <div className="ag-relative">
            <video
              src={selectedBackground.replace('video;', '')}
              muted
              loop
              autoPlay
              onMouseOver={handlePlayVideo}
              onMouseOut={handlePauseVideo}
              className={cn(
                'ag-w-full ag-h-custom-72 ag-rounded-lg ag-border ag-cursor-pointer ag-object-cover',
                selectedBackground
                  ? 'ag-border-[3px] ag-border-primary-600'
                  : theme === 'dark'
                  ? 'ag-border-neutral-700'
                  : 'ag-border-neutral-200'
              )}
            />
            <button
              onClick={() => onChange('')}
              className="ag-absolute ag-top-2 ag-right-2 ag-bg-white ag-text-black ag-rounded-full ag-w-6 ag-h-6 ag-flex ag-items-center ag-justify-center ag-shadow-sm hover:ag-bg-gray-100"
              title="Remove selected video"
            >
              <IoMdClose size={18} color="#000" />
            </button>
          </div>
        )}

        {customVideoBackgrounds.slice(0, 3).map((video) => (
          <video
            key={video}
            muted
            onMouseOver={handlePlayVideo}
            onMouseOut={handlePauseVideo}
            onClick={() => onChange(video)}
            src={video.replace('video;', '')}
            className={cn(
              'ag-w-full ag-h-custom-72 ag-rounded-lg ag-border ag-cursor-pointer ag-object-cover',
              selectedBackground === video
                ? 'ag-border-[3px] ag-border-primary-600'
                : theme === 'dark'
                ? 'ag-border-neutral-700'
                : 'ag-border-neutral-200'
            )}
          />
        ))}

        {stockVideos.slice(0, 9).map((video, index) => {
          const fullHdVideo = video.video_files.find(
            (item) => item.width === 1920
          );
          const src = fullHdVideo?.link ?? video.video_files[0].link;
          const value = `video;${src}`;
          const isSelected = selectedBackground === value;

          return (
            <video
              key={video.id}
              ref={stockVideos.length - 1 === index ? sentinelRef : null}
              muted
              onMouseOver={handlePlayVideo}
              onMouseOut={handlePauseVideo}
              onClick={() => onChange(value)}
              src={src}
              className={cn(
                'ag-w-full ag-h-custom-72 ag-rounded-lg ag-border ag-cursor-pointer ag-object-cover',
                isSelected
                  ? 'ag-border-[3px] ag-border-primary-600'
                  : theme === 'dark'
                  ? 'ag-border-neutral-700'
                  : 'ag-border-neutral-200'
              )}
            />
          );
        })}
      </div>

      <Modal
        setOpen={setOpen}
        open={open}
        noPadding
        positionClasses="ag-inset-0"
        rootClassName="!ag-z-[60]"
        className="ag-relative ag-transform ag-rounded-lg ag-text-left ag-shadow-xl ag-transition-all ag-w-[62rem]"
      >
        <div className="ag-p-6 ag-flex ag-flex-col md:h-[45rem]">
          <div className="ag-mb-4">
            <SearchInput
              text="Search a video"
              onChange={handleSearchVideo}
              theme={theme}
            />
          </div>
          <div
            ref={scrollRef}
            className="ag-flex-1 ag-overflow-y-auto"
            style={{ overflowY: 'auto', paddingRight: '0.25rem' }}
          >
            <div className="ag-grid ag-grid-cols-4 ag-gap-3 ag-mt-4">
              {stockVideos.map((video, index) => {
                const fullHd =
                  video.video_files.find((f) => f.width === 1920) ||
                  video.video_files[0];
                const src = fullHd.link;
                const value = `video;${src}`;
                const isSelected = selectedBackground === value;
                const isLast = index === stockVideos.length - 1;

                return (
                  <video
                    key={`${video.id}-${index}`}
                    ref={isLast ? sentinelRef : null}
                    onClick={() => onChange(value)}
                    onMouseOver={handlePlayVideo}
                    onMouseOut={handlePauseVideo}
                    src={src}
                    muted
                    loop
                    className={cn(
                      'ag-w-full ag-h-custom-72 ag-aspect-square ag-rounded-lg ag-border ag-cursor-pointer ag-object-cover',
                      isSelected
                        ? 'ag-border-[3px] ag-border-primary-600'
                        : theme === 'dark'
                        ? 'ag-border-neutral-700'
                        : 'ag-border-neutral-200'
                    )}
                  />
                );
              })}
              {!isVideoLoaded && (
                <div className="ag-col-span-full ag-flex ag-justify-center ag-my-4">
                  <Loader size={50} />
                </div>
              )}
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default VideosTab;
